import {
  Box,
  Button,
  Stack,
  Typography,
  LinearProgress,
  IconButton,
  Chip,
  Collapse,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UserAvatar from "../../../../../common/UserAvatar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PostAddIcon from "@mui/icons-material/PostAdd";
import UndoIcon from "@mui/icons-material/Undo";
import QuizIcon from "../../../../../../assets/icons/quiz-icon-overview.svg";
import { StyledPaper } from "../../../../../../customComponents/styled/styledPaper";
import {
  LessonPlanLabel,
  LessonPlanValue,
} from "../../../../../../customComponents/styled/StyledTypgraphies";
import {
  useGetQuizSubmissionsQuery,
  useGetSubmissionDetailQuery,
  useGetQuizByIdQuery,
} from "../../../../../../app/quizApiSlice";
import { useCheckQuizPostedQuery } from "../../../../quick-entry/recordApiSlice";
import MathContent from "../../../../../customComponents/MathContent";
import PostToRecordModal from "./PostToRecordModal";
import RevertPostingModal from "./RevertPostingModal";

const PER_PAGE = 20;

// Lazy-loads and renders the per-question breakdown for one submission
const SubmissionDetail = ({ quizId, userId, totalQuestions }) => {
  const { data, isLoading } = useGetSubmissionDetailQuery(
    { quiz_id: quizId, user_id: userId },
    { skip: !quizId || !userId }
  );

  const answers = data?.answers || [];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  let correct = 0;
  let skipped = 0;
  answers.forEach((a) => {
    if (!a.option_id) {
      skipped++;
    } else {
      const chosen = a.question?.options?.find((o) => o.id === a.option_id);
      if (chosen?.is_answer) correct++;
    }
  });
  const total = totalQuestions || answers.length;
  const wrong = answers.length - correct - skipped;

  return (
    <Box
      sx={{
        borderTop: (theme) =>
          `1px solid ${theme.palette.mode === "dark" ? "#333" : "#F0F0F0"}`,
        px: { xs: 2, md: 4 },
        py: 2,
      }}
    >
      {/* Score summary row */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(39, 174, 96, 0.14)"
                : "#F0FFF4",
            px: 2.5,
            py: 1.2,
            borderRadius: "10px",
          }}
        >
          <CheckCircleIcon sx={{ color: "#27AE60", fontSize: "1.1rem" }} />
          <Typography sx={{ fontWeight: 700, color: "#27AE60" }}>
            {correct}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Correct
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(229, 57, 53, 0.14)"
                : "#FFF5F5",
            px: 2.5,
            py: 1.2,
            borderRadius: "10px",
          }}
        >
          <CancelIcon sx={{ color: "#E53935", fontSize: "1.1rem" }} />
          <Typography sx={{ fontWeight: 700, color: "#E53935" }}>
            {wrong}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Wrong
          </Typography>
        </Box>
        {skipped > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(245,158,11,0.14)"
                  : "#FFFBEB",
              px: 2.5,
              py: 1.2,
              borderRadius: "10px",
            }}
          >
            <RemoveCircleOutlineIcon
              sx={{ color: "#F59E0B", fontSize: "1.1rem" }}
            />
            <Typography sx={{ fontWeight: 700, color: "#F59E0B" }}>
              {skipped}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Skipped
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "#F5F5F5",
            px: 2.5,
            py: 1.2,
            borderRadius: "10px",
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>{total}</Typography>
          <Typography variant="body2" color="text.secondary">
            Total
          </Typography>
        </Box>
      </Box>

      {/* Questions */}
      <Stack gap={2}>
        {answers.map((answer, idx) => {
          const options = answer.question?.options || [];
          const isSkipped = !answer.option_id;
          const chosenOption = isSkipped
            ? null
            : options.find((o) => o.id === answer.option_id);
          const isCorrect = !isSkipped && chosenOption?.is_answer === true;
          const borderAccent = isSkipped
            ? "#F59E0B"
            : isCorrect
            ? "#27AE60"
            : "#E53935";

          return (
            <Box
              key={answer.id || idx}
              sx={{ borderLeft: `4px solid ${borderAccent}`, pl: 2, py: 1 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    minWidth: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: borderAccent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: "2px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: ".75rem",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {idx + 1}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    fontSize: ".9rem",
                    color: "text.primary",
                    "& p": { m: 0 },
                    "& img": { maxWidth: "100%" },
                  }}
                >
                  <MathContent html={answer.question?.body || ""} />
                </Box>
                {isSkipped && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexShrink: 0,
                      mt: "3px",
                    }}
                  >
                    <RemoveCircleOutlineIcon
                      sx={{ color: "#F59E0B", fontSize: "1rem" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#F59E0B",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Not answered
                    </Typography>
                  </Box>
                )}
              </Box>

              <Stack gap={0.75} pl={4}>
                {options.map((opt) => {
                  const isChosen = opt.id === answer.option_id;
                  const isAnswerKey = opt.is_answer === true;
                  let bgcolor = "transparent";
                  let borderColor = "#E0E0E0";
                  let textColor = "text.primary";
                  if (isChosen && isAnswerKey) {
                    bgcolor = "#E8F5E9";
                    borderColor = "#27AE60";
                    textColor = "#27AE60";
                  } else if (isChosen && !isAnswerKey) {
                    bgcolor = "#FFEBEE";
                    borderColor = "#E53935";
                    textColor = "#E53935";
                  } else if (!isChosen && isAnswerKey) {
                    bgcolor = "#F0FFF4";
                    borderColor = "#A5D6A7";
                    textColor = "#27AE60";
                  }

                  return (
                    <Box
                      key={opt.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 0.8,
                        borderRadius: "6px",
                        border: `1px solid ${borderColor}`,
                        bgcolor: (theme) => {
                          if (bgcolor === "#E8F5E9")
                            return theme.palette.mode === "dark"
                              ? "rgba(39, 174, 96, 0.16)"
                              : bgcolor;
                          if (bgcolor === "#FFEBEE")
                            return theme.palette.mode === "dark"
                              ? "rgba(229, 57, 53, 0.16)"
                              : bgcolor;
                          if (bgcolor === "#F0FFF4")
                            return theme.palette.mode === "dark"
                              ? "rgba(39, 174, 96, 0.12)"
                              : bgcolor;
                          return bgcolor;
                        },
                      }}
                    >
                      {isChosen && isAnswerKey && (
                        <CheckCircleIcon
                          sx={{ fontSize: "1rem", color: "#27AE60" }}
                        />
                      )}
                      {isChosen && !isAnswerKey && (
                        <CancelIcon
                          sx={{ fontSize: "1rem", color: "#E53935" }}
                        />
                      )}
                      {!isChosen && isAnswerKey && (
                        <CheckCircleIcon
                          sx={{ fontSize: "1rem", color: "#A5D6A7" }}
                        />
                      )}
                      {!isChosen && !isAnswerKey && (
                        <Box sx={{ width: 16 }} />
                      )}
                      <Box
                        sx={{
                          fontSize: ".85rem",
                          color: textColor,
                          "& p": { m: 0 },
                          "& img": { maxWidth: "100%" },
                        }}
                      >
                        <MathContent html={opt.body || ""} />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const QuizSubmissions = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [postToRecordOpen, setPostToRecordOpen] = useState(false);
  const [revertPostingOpen, setRevertPostingOpen] = useState(false);

  // Infinite scroll state
  const [page, setPage] = useState(1);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);

  const { data: quizData, isLoading: isLoadingQuiz } = useGetQuizByIdQuery(quizId, {
    skip: !quizId,
  });
  const {
    data: submissionsData,
    isLoading: isLoadingSubmissions,
    isFetching,
    isError,
  } = useGetQuizSubmissionsQuery(
    { quiz_id: quizId, page, per_page: PER_PAGE },
    { skip: !quizId }
  );
  const { data: quizPostedData } = useCheckQuizPostedQuery(quizId, {
    skip: !quizId,
  });
  const isPostedToRecords = quizPostedData?.posted ?? false;

  const quiz = quizData?.quiz || quizData || null;

  // Accumulate pages
  useEffect(() => {
    if (!submissionsData) return;
    const incoming = submissionsData.submissions || [];
    const pagination = submissionsData.pagination || {};
    setAllSubmissions((prev) => {
      const existingIds = new Set(prev.map((s) => s.user_id));
      const fresh = incoming.filter((s) => !existingIds.has(s.user_id));
      return [...prev, ...fresh];
    });
    setHasMore(page < (pagination.pages || 1));
  }, [submissionsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver sentinel
  const handleObserver = useCallback(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !isFetching) {
        setPage((p) => p + 1);
      }
    },
    [hasMore, isFetching]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const totalSubmissions = submissionsData?.pagination?.total ?? allSubmissions.length;
  const isLoading = isLoadingQuiz || (isLoadingSubmissions && page === 1);

  return (
    <Box>
      {/* Back nav */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            Back to Reports
          </Typography>
        </Box>
        {totalSubmissions > 0 && (
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              startIcon={<PostAddIcon />}
              onClick={() => setPostToRecordOpen(true)}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Record
            </Button>
            {isPostedToRecords && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<UndoIcon />}
                onClick={() => setRevertPostingOpen(true)}
                sx={{ textTransform: "none", borderRadius: "10px" }}
              >
                Revert
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <PostToRecordModal
        open={postToRecordOpen}
        onClose={() => setPostToRecordOpen(false)}
        quiz={quiz}
        submissions={allSubmissions}
      />

      <RevertPostingModal
        open={revertPostingOpen}
        onClose={() => setRevertPostingOpen(false)}
        quiz={quiz}
      />

      {isLoading && <LinearProgress />}

      {/* Quiz header */}
      {quiz && (
        <StyledPaper sx={{ py: 4, px: { xs: 2, md: 6 }, mb: 3 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={3}>
            <img
              src={QuizIcon}
              style={{ height: "24px", width: "24px" }}
              alt="quiz"
            />
            <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
              {quiz.subject} Quiz
            </Typography>
          </Stack>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              rowGap: 3,
              ">*": { minWidth: "33%", flex: "1 1 33%" },
            }}
          >
            <Stack>
              <LessonPlanLabel>Subject</LessonPlanLabel>
              <LessonPlanValue>{quiz.subject}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Level</LessonPlanLabel>
              <LessonPlanValue>{quiz.level}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Type</LessonPlanLabel>
              <LessonPlanValue sx={{ textTransform: "capitalize" }}>
                {quiz.type}
              </LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Questions</LessonPlanLabel>
              <LessonPlanValue>{quiz.questions?.length || 0}</LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Students</LessonPlanLabel>
              <LessonPlanValue>
                {quiz.participants?.length || 0}
              </LessonPlanValue>
            </Stack>
            <Stack>
              <LessonPlanLabel>Date</LessonPlanLabel>
              <LessonPlanValue>{formatDate(quiz.updated_at)}</LessonPlanValue>
            </Stack>
          </Box>
        </StyledPaper>
      )}

      {/* Submissions heading */}
      <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 2 }}>
        Submissions ({totalSubmissions})
      </Typography>

      {isError && (
        <Box textAlign="center" py={4}>
          <Typography color="error">
            Failed to load submissions. Please try again.
          </Typography>
        </Box>
      )}

      {!isLoading && !isError && allSubmissions.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">No submissions yet.</Typography>
        </Box>
      )}

      {/* Student submission cards */}
      <Stack gap={2}>
        {allSubmissions.map((submission) => {
          const percent =
            submission.total_count > 0
              ? Math.round(
                  (submission.correct_count / submission.total_count) * 100
                )
              : 0;
          const isExpanded = expandedUserId === submission.user_id;
          const user = submission.user || {};

          return (
            <Box
              key={submission.user_id}
              sx={{
                border: "1px solid #DAD9D7",
                borderRadius: "11px",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                overflow: "hidden",
              }}
            >
              {/* Student row — click to expand */}
              <Box
                onClick={() =>
                  setExpandedUserId(isExpanded ? null : submission.user_id)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: { xs: "1em", md: "1.2em 2em" },
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#FAFAFA",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <UserAvatar
                    src={user.photo || undefined}
                    name={`${user.first_name || ""} ${user.last_name || ""}`}
                    sx={{
                      width: 42,
                      height: 42,
                      fontSize: "1rem",
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: ".95rem",
                        color: "text.primary",
                      }}
                    >
                      {user.first_name} {user.last_name}
                    </Typography>
                    {user.reg_number && (
                      <Typography
                        sx={{
                          fontSize: ".75rem",
                          color: "text.secondary",
                          fontWeight: 500,
                        }}
                      >
                        {user.reg_number}
                      </Typography>
                    )}
                    <Typography
                      sx={{ fontSize: ".8rem", color: "text.secondary" }}
                    >
                      {user.email || user.username}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.8,
                      borderRadius: "8px",
                      backgroundColor: (theme) =>
                        percent >= 50
                          ? theme.palette.mode === "dark"
                            ? "rgba(39, 174, 96, 0.16)"
                            : "#E8F5E9"
                          : theme.palette.mode === "dark"
                          ? "rgba(229, 57, 53, 0.16)"
                          : "#FFEBEE",
                      minWidth: "64px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: percent >= 50 ? "#27AE60" : "#E53935",
                      }}
                    >
                      {percent}%
                    </Typography>
                    <Typography
                      sx={{ fontSize: ".7rem", color: "text.secondary" }}
                    >
                      {submission.correct_count}/{submission.total_count}
                    </Typography>
                  </Box>

                  <Chip
                    label={formatDate(submission.updated_at)}
                    size="small"
                    sx={{
                      fontSize: ".75rem",
                      color: "text.secondary",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark" ? "#2a2a2a" : "#F5F5F5",
                    }}
                  />
                  <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>

              {/* Per-question breakdown — loaded lazily on expand */}
              <Collapse in={isExpanded}>
                {isExpanded && (
                  <SubmissionDetail
                    quizId={quizId}
                    userId={submission.user_id}
                    totalQuestions={quiz?.questions?.length}
                  />
                )}
              </Collapse>
            </Box>
          );
        })}
      </Stack>

      {/* Infinite scroll sentinel */}
      <Box ref={sentinelRef} sx={{ py: 2, display: "flex", justifyContent: "center" }}>
        {isFetching && page > 1 && <CircularProgress size={24} />}
      </Box>
    </Box>
  );
};

export default QuizSubmissions;
